<template>
  <div class="services-page">
    <section class="page-hero services-hero">
      <div class="container services-hero">
        <div class="services-hero__summary-bar">
          <span class="eyebrow">{{ content.eyebrow }}</span>
          <p class="page-copy">{{ content.title }}</p>
          <p class="services-hero__note">{{ content.intro }}</p>
        </div>
      </div>
    </section>

    <section class="section services-story">
      <div class="container services-story__shell">
        <div class="card-panel services-story__intro">
          <span class="eyebrow">{{ content.storyEyebrow }}</span>
          <h2 class="section-title">{{ content.storyTitle }}</h2>
          <p v-for="line in content.storyIntro" :key="line" class="services-story__lead">{{ line }}</p>
        </div>

        <div class="services-story__rail">
          <article
            v-for="(section, index) in content.storySections"
            :key="section.title"
            :class="[
              'card-panel services-story__section',
              { 'services-story__section--emphasis': index === 0 }
            ]"
          >
            <h3>{{ section.title }}</h3>
            <p v-for="line in section.body" :key="line">{{ line }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section-tight services-offerings">
      <div class="container">
        <div class="card-panel services-offerings__panel">
          <div class="services-offerings__header">
            <span class="eyebrow">{{ content.highlightsEyebrow }}</span>
            <h2 class="section-title">{{ content.highlightsTitle }}</h2>
          </div>
          <div class="services-highlights__grid">
            <div
              v-for="item in content.highlights"
              :key="item"
              class="services-highlights__item"
            >
              <span>{{ item }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section services-fleet">
      <div class="container">
        <div class="services-fleet__header card-panel">
          <span class="eyebrow">{{ content.fleetEyebrow }}</span>
          <h2 class="section-title">{{ content.fleetTitle }}</h2>
          <p class="section-copy">{{ content.fleetCopy }}</p>
        </div>
      </div>

      <div class="services-fleet__viewport">
        <div class="services-fleet__grid">
          <article
            v-for="vehicle in content.vehicles"
            :key="vehicle.name"
            class="card-panel services-fleet__card"
          >
            <div class="services-fleet__image-wrap">
              <img :src="vehicle.image" :alt="vehicle.alt" class="services-fleet__image">
            </div>
            <div class="services-fleet__body">
              <span class="services-fleet__capacity">{{ vehicle.capacity }}</span>
              <strong>{{ vehicle.name }}</strong>
              <p v-for="line in vehicle.description" :key="line">{{ line }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
var servicesContent = {
  en: {
    eyebrow: 'SERVICES',
    title: 'Inbound travel services designed around purpose, logistics, and guest experience across Japan.',
    intro: 'From vehicle planning to fully structured itineraries, JBC TOURS builds travel programs that align transport, operations, and on-the-ground delivery for international groups.',
    fleetEyebrow: 'Vehicle Lineup',
    fleetTitle: 'A fleet designed for smooth transfers, inspections, and group journeys.',
    fleetCopy: '',
    vehicles: [
      {
        image: '/images/services1.png',
        alt: 'Alphard luxury business van',
        capacity: 'Capacity: 7 seats (1–4 passengers recommended)',
        name: 'Alphard (Luxury Business Van)',
        description: [
          'A premium vehicle offering exceptional comfort and style.',
          'Ideal for business travel and upscale private journeys, delivering a refined and exclusive travel experience.'
        ]
      },
      {
        image: '/images/services2.png',
        alt: 'Hiace executive van',
        capacity: 'Capacity: 10 seats (5–7 passengers recommended)',
        name: 'Hiace (Executive Van)',
        description: [
          'A spacious and durable van designed for versatility.',
          'Equipped with advanced safety features, making it ideal for small groups and comfortable transfers.'
        ]
      },
      {
        image: '/images/services3.png',
        alt: 'Micro bus for group travel',
        capacity: 'Capacity: 17 seats (8–13 passengers recommended)',
        name: 'Micro Bus',
        description: [
          'Compact yet comfortable, perfect for small group travel.',
          'Offers excellent maneuverability and flexibility for city and regional tours.'
        ]
      },
      {
        image: '/images/services4.png',
        alt: 'Mid-size bus for medium groups',
        capacity: 'Capacity: 27 seats (14–22 passengers recommended)',
        name: 'Mid-size Bus',
        description: [
          'A well-balanced option for medium-sized groups.',
          'Provides ample space and convenience for a smooth and comfortable journey.'
        ]
      },
      {
        image: '/images/services5.png',
        alt: 'Large coach for long-distance group travel',
        capacity: 'Capacity: 45 or 49 seats',
        name: 'Large Bus (Coach)',
        description: [
          '45-seater: ideal for 23–39 passengers',
          '49-seater: ideal for 40–45 passengers',
          'Spacious and fully equipped, ensuring a comfortable experience even on long-distance journeys.'
        ]
      }
    ],
    storyEyebrow: 'Business Overview',
    storyTitle: 'Travel architecture for inbound programs that require both flexibility and reliable execution.',
    storyIntro: [
      'Japan Business Consulting Co., Ltd. is headquartered in Osaka, with an office in Tokyo, and operates inbound travel services under its licensed travel agency registration in Japan.',
      'We are not merely a travel arrangement company. We position ourselves as a travel design partner, dedicated to creating fully tailored, purpose-driven travel experiences for international visitors to Japan.',
      'Rather than offering standardized packages, we design bespoke, experience-oriented journeys aligned with each client’s objectives—whether for business, investment, inspection visits, VIP travel, cultural exploration, or medical and wellness purposes.'
    ],
    storySections: [
      {
        title: 'Global Network & Integrated Operations',
        body: [
          'Through close partnerships with leading travel agencies in other countries, we provide end-to-end coordination from planning to on-the-ground execution across Japan.',
          'Leveraging our nationwide network of hotels, transportation providers, and professional guides, we deliver fully integrated travel solutions—seamlessly connecting accommodation, transport, and curated experiences into a single, cohesive journey.',
          'Our multilingual travel consultants work closely with clients throughout the entire process, ensuring smooth communication, operational reliability, and high-quality service delivery at every stage.'
        ]
      },
      {
        title: 'Our Value Proposition',
        body: [
          'Our strength lies not in simple travel arrangement, but in travel architecture.',
          'In a rapidly evolving inbound tourism market, we go beyond traditional sightseeing services to design structured, goal-oriented travel experiences that combine logistics, local expertise, and cultural insight into one unified journey.',
          'We focus on delivering meaningful value—transforming travel from a product into a carefully crafted experience with purpose and impact.'
        ]
      },
      {
        title: 'Flexible Travel Design',
        body: [
          'In addition to fully tailor-made itineraries, we also offer curated model programs designed for various travel objectives.',
          'From individual travelers to corporate groups and incentive programs(MICE), we provide flexible solutions that adapt to different expectations, timelines, and budgets.',
          'We believe travel is not simply about moving from one place to another, but about creating meaningful, well-designed experiences that leave a lasting impression.'
        ]
      }
    ],
    highlightsEyebrow: 'Core Service Areas',
    highlightsTitle: 'Business Overview (Inbound Travel Services)',
    highlights: [
      'VIP Private Travel',
      'Business Inspection & Corporate Study Tours',
      'Medical & Wellness Travel Coordination',
      'Cultural Experience & Regional Engagement Programs',
      'Fully Customized Travel Planning, Quotation & Operations'
    ]
  },
  ja: {
    eyebrow: '事業内容',
    title: '車両手配からオーダーメイド旅行設計まで、訪日インバウンドを一貫して支えるサービスです。',
    intro: '人数や目的に応じた車両手配に加え、企画・宿泊・移動・体験をつないだ旅行全体の設計と運営を行います。',
    fleetEyebrow: '車',
    fleetTitle: '目的や人数に合わせて選べる車両ラインアップ。',
    fleetCopy: '',
    vehicles: [
      {
        image: '/images/services1.png',
        alt: 'アルファード車両イメージ',
        capacity: '7席(1〜4人乗り)',
        name: 'アルファード',
        description: [
          'プレミアムな快適さを備えた',
          '高級ビジネスカー 非日常の旅行体験'
        ]
      },
      {
        image: '/images/services2.png',
        alt: 'ハイエース車両イメージ',
        capacity: '10席(5～7人乗り)',
        name: 'ハイエース',
        description: [
          '広々とした耐久性のあるバン。',
          '幅広い耐久性と高度な安全技術を備えています。'
        ]
      },
      {
        image: '/images/services3.png',
        alt: 'ミニバス車両イメージ',
        capacity: '17席(8～13人乗り)',
        name: 'ミニバス',
        description: [
          'コンパクトで快適で、少人数のグループに最適です。',
          '優れた操縦性を備えています。'
        ]
      },
      {
        image: '/images/services4.png',
        alt: '中型バス車両イメージ',
        capacity: '27席(14～22人乗り)',
        name: '中型バス',
        description: [
          '中規模のグループ向けの',
          'バランスの取れたオプションで、',
          'スペースと利便性を提供します。'
        ]
      },
      {
        image: '/images/services5.png',
        alt: '大型バス車両イメージ',
        capacity: '45席または49席',
        name: '大型バス',
        description: [
          '45人乗り: 23〜39名が最適',
          '49人乗り: 40〜45名が最適',
          '広々としていて設備が整っており、',
          '長旅でも快適な体験をお約束します。'
        ]
      }
    ],
    storyEyebrow: 'サービス概要',
    storyTitle: '目的起点で旅を設計し、全国の運営ネットワークで実行まで支えます。',
    storyIntro: [
      'JBC TOURSは、大阪に本社、東京に拠点を構え、旅行業登録に基づき訪日インバウンド旅行の企画・運営を専門に行っています。',
      '私たちは単なる旅行手配会社ではなく、「旅を設計するパートナー」として、訪日ゲスト一人ひとりの目的に最適化された体験をデザインすることを使命としています。',
      'ビジネス視察、VIP対応、文化体験、医療・ウェルネスなど、多様化する訪日ニーズに対し、画一的なパッケージではなく、目的起点で設計されたオーダーメイド型の旅を提供しています。'
    ],
    storySections: [
      {
        title: 'グローバルネットワークによる一貫サポート',
        body: [
          '信頼ある海外パートナー企業と連携し、企画段階から現地運営までを一貫してサポートしています。',
          '日本全国に広がるホテル、バス会社、通訳ガイドとのネットワークを活かし、宿泊・交通・体験を分断することなく、シームレスに統合された旅行体験を実現しています。',
          'また、多言語対応の旅行コンサルタントが、企画・提案から現地対応までを一貫してサポートし、文化や言語の壁を超えたスムーズなコミュニケーションを提供します。'
        ]
      },
      {
        title: '私たちが提供する価値',
        body: [
          '私たちは手配力のみならず、設計力を強みとしております。',
          '市場変化の激しいインバウンド領域において、単なる観光ではなく、目的達成のための移動・滞在・体験を統合的に設計することで、付加価値の高い旅行体験を創出しています。'
        ]
      },
      {
        title: 'フレキシブルな旅の設計',
        body: [
          '完全オーダーメイドのプランニングに加え、目的別のモデルプランも提供しており、個人旅行から法人インセンティブまで柔軟に対応しています。',
          'お客様にご満足いただけるまで細部にわたり丁寧に調整を行い、ご旅行の開始から終了まで一貫したサポートを提供いたします。'
        ]
      }
    ],
    highlightsEyebrow: '主なサービス領域',
    highlightsTitle: '旅の目的に応じて設計する提供領域',
    highlights: [
      'VIPプライベートトラベル（富裕層・要人対応）',
      'MICE(ビジネス視察・研修旅行・インセンティブツアー)の企画・運営',
      '医療・ウェルネスツーリズムの手配',
      '文化/教育体験・地域交流プログラムの設計',
      'オーダーメイド旅行の企画・見積・運営'
    ]
  }
};

export default {
  name: 'Services',
  props: {
    language: {
      type: String,
      default: 'en'
    }
  },
  computed: {
    content: function () {
      return servicesContent[this.language] || servicesContent.en;
    }
  }
};
</script>

<style scoped>
.services-hero {
  padding-bottom: 24px;
}

.services-hero__summary-bar,
.services-fleet__header {
  padding: 38px;
}

.services-hero__summary-bar {
  display: grid;
  gap: 8px 18px;
  grid-template-columns: auto minmax(0, 1.2fr) minmax(280px, 0.9fr);
  align-items: center;
  max-width: 1180px;
  padding: 18px 24px;
  border-radius: 18px;
  border: 1px solid rgba(15, 76, 129, 0.08);
  background: linear-gradient(90deg, rgba(15, 76, 129, 0.04), rgba(255, 255, 255, 0.9));
  box-shadow: none;
}

.services-hero__summary-bar .page-copy {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.7;
}

.services-hero__note {
  margin: 0;
  color: var(--color-muted);
  line-height: 1.7;
}

.services-fleet {
  padding-top: 0;
}

.services-fleet__header {
  margin-bottom: 18px;
}

.services-fleet__viewport {
  width: min(calc(100% - 32px), 1480px);
  margin: 0 auto;
}

.services-fleet__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.services-fleet__card {
  display: grid;
  grid-template-columns: minmax(0, 360px) minmax(0, 1fr);
  gap: 24px;
  padding: 28px;
  align-items: center;
}

.services-fleet__image-wrap {
  overflow: hidden;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.72);
}

.services-fleet__image {
  width: 100%;
  height: 280px;
  object-fit: cover;
}

.services-fleet__body strong {
  display: block;
  margin: 8px 0 12px;
  font-size: 24px;
}

.services-fleet__capacity {
  display: inline-flex;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(15, 76, 129, 0.08);
  color: var(--color-accent-deep);
  font-size: 12px;
  letter-spacing: 0.08em;
}

.services-fleet__body p,
.services-story__section p,
.services-story__lead {
  margin: 0 0 10px;
  color: var(--color-muted);
  line-height: 1.8;
}

.services-story__shell {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 28px;
  align-items: start;
}

.services-story__intro,
.services-story__section,
.services-offerings__panel {
  padding: 34px;
}

.services-story__intro {
  position: sticky;
  top: 24px;
  background: linear-gradient(180deg, rgba(15, 76, 129, 0.08), rgba(255, 255, 255, 0.96));
}

.services-story__lead {
  max-width: 58ch;
}

.services-story__rail {
  display: grid;
  gap: 20px;
}

.services-story__section {
  position: relative;
  overflow: hidden;
}

.services-story__section::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: rgba(15, 76, 129, 0.12);
}

.services-story__section--emphasis {
  background: linear-gradient(135deg, rgba(15, 76, 129, 0.12), rgba(255, 255, 255, 0.98));
}

.services-story__section--emphasis::before {
  background: var(--color-accent-deep);
}

.services-story__section h3 {
  margin: 0 0 14px;
  font-size: 22px;
}

.services-offerings__panel {
  display: grid;
  gap: 26px;
}

.services-offerings__header {
  max-width: 760px;
}

.services-highlights__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.services-highlights__item {
  padding: 20px 22px;
  border-radius: 20px;
  background: rgba(15, 76, 129, 0.06);
  border: 1px solid rgba(15, 76, 129, 0.08);
}

.services-highlights__item span {
  display: block;
  color: var(--color-text);
  line-height: 1.7;
}

@media (max-width: 1023px) {
  .services-hero__summary-bar {
    grid-template-columns: 1fr;
    align-items: start;
  }

  .services-fleet__viewport {
    width: min(calc(100% - 32px), 100%);
  }

  .services-fleet__grid,
  .services-highlights__grid,
  .services-story__shell {
    grid-template-columns: 1fr;
  }

  .services-story__intro {
    position: static;
  }
}

@media (max-width: 767px) {
  .services-hero__summary-bar,
  .services-fleet__header,
  .services-fleet__card,
  .services-story__intro,
  .services-story__section,
  .services-offerings__panel {
    padding: 22px;
  }

  .services-fleet__viewport {
    width: min(calc(100% - 24px), 100%);
  }

  .services-fleet__card {
    grid-template-columns: 1fr;
  }

  .services-fleet__image {
    height: 240px;
  }

  .services-fleet__body strong,
  .services-story__section h3 {
    font-size: 20px;
  }
}
</style>
